import { jsPDF } from 'jspdf';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const STATUS_LABELS = {
  inward: 'Inward',
  in_progress: 'In Progress',
  outward: 'Outward',
  completed: 'Completed'
};

const formatDate = (value) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return date.toLocaleDateString();
};

const formatSize = (bytes) => {
  const size = Number(bytes) || 0;
  if (size <= 0) return '-';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export const isExternalLink = (link) => /^https?:\/\//i.test(String(link || ''));

const safeBaseName = (value, fallback = 'client') =>
  String(value || fallback)
    .replace(/[^\w.\-() ]+/g, '_')
    .replace(/\s+/g, '_')
    .slice(0, 80);

const guessExtension = (filename, contentType, url) => {
  const fromName = String(filename || '').match(/\.([a-z0-9]{1,8})$/i);
  if (fromName) return `.${fromName[1].toLowerCase()}`;

  const typeMap = {
    'application/pdf': '.pdf',
    'image/jpeg': '.jpg',
    'image/png': '.png',
    'image/webp': '.webp',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/vnd.ms-excel': '.xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
    'text/plain': '.txt'
  };
  if (contentType && typeMap[contentType.split(';')[0].trim()]) {
    return typeMap[contentType.split(';')[0].trim()];
  }

  try {
    const pathname = new URL(url).pathname;
    const fromUrl = pathname.match(/\.([a-z0-9]{1,8})$/i);
    if (fromUrl) return `.${fromUrl[1].toLowerCase()}`;
  } catch {
    // ignore
  }
  return '';
};

const uniqueZipName = (used, preferred) => {
  let name = preferred;
  let i = 2;
  while (used.has(name.toLowerCase())) {
    const dot = preferred.lastIndexOf('.');
    if (dot > 0) {
      name = `${preferred.slice(0, dot)}_${i}${preferred.slice(dot)}`;
    } else {
      name = `${preferred}_${i}`;
    }
    i += 1;
  }
  used.add(name.toLowerCase());
  return name;
};

/**
 * Build client profile PDF as a Blob (does not trigger download).
 */
export function buildClientPdfBlob(client, attachments = [], packageNotes = []) {
  if (!client) {
    throw new Error('Client details are required');
  }

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 48;
  const maxWidth = pageWidth - marginX * 2;
  let y = 52;

  const ensureSpace = (needed = 24) => {
    if (y + needed <= pageHeight - 48) return;
    doc.addPage();
    y = 52;
  };

  const drawSectionTitle = (title) => {
    ensureSpace(36);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(15, 23, 42);
    doc.text(title, marginX, y);
    y += 8;
    doc.setDrawColor(37, 99, 235);
    doc.setLineWidth(1.5);
    doc.line(marginX, y, marginX + 72, y);
    y += 18;
  };

  const drawField = (label, value) => {
    ensureSpace(28);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(String(label).toUpperCase(), marginX, y);
    y += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    const lines = doc.splitTextToSize(String(value || '-'), maxWidth);
    doc.text(lines, marginX, y);
    y += lines.length * 14 + 10;
  };

  doc.setFillColor(49, 130, 206);
  doc.rect(0, 0, pageWidth, 78, 'F');
  doc.setFillColor(66, 153, 225);
  doc.rect(0, 0, 10, 78, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Client Management System', marginX, 32);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(235, 248, 255);
  doc.text('Client Profile Report', marginX, 50);
  doc.text(`Generated ${new Date().toLocaleString()}`, marginX, 66);

  y = 108;

  doc.setTextColor(26, 54, 93);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  const nameLines = doc.splitTextToSize(client.client_name || 'Untitled Client', maxWidth);
  doc.text(nameLines, marginX, y);
  y += nameLines.length * 22 + 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(71, 85, 105);
  doc.text(
    `${client.client_id || '-'}  ·  ${client.customer_area || '-'}  ·  ${client.firm_name || '-'}`,
    marginX,
    y
  );
  y += 18;

  const status = STATUS_LABELS[client.status] || client.status || 'Inward';
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(37, 99, 235);
  doc.text(`Status: ${status}`, marginX, y);
  y += 28;

  drawSectionTitle('Client Details');

  const fields = [
    ['Client ID', client.client_id],
    ['Client Name', client.client_name],
    ['Mobile Number', client.mobile_number],
    ['Customer Area', client.customer_area],
    ['Firm Name', client.firm_name],
    ['Plot No', client.plot_no],
    ['Plot Area', `${client.plot_area ?? '-'} ${client.plot_area_unit || ''}`.trim()],
    ['Project Head', client.project_head],
    ['Assigned Architect', client.assigned_architect || '-'],
    ['Status', status],
    ['Inward Date', formatDate(client.inward_date)],
    ['Outward Date', formatDate(client.outward_date)]
  ];

  const colGap = 24;
  const colWidth = (maxWidth - colGap) / 2;
  let col = 0;
  let rowTop = y;

  fields.forEach(([label, value]) => {
    const x = marginX + col * (colWidth + colGap);
    if (col === 0) {
      ensureSpace(48);
      rowTop = y;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(String(label).toUpperCase(), x, rowTop);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    const lines = doc.splitTextToSize(String(value || '-'), colWidth);
    doc.text(lines, x, rowTop + 14);

    if (col === 1) {
      y = rowTop + Math.max(28, lines.length * 12 + 22);
      col = 0;
    } else {
      col = 1;
    }
  });

  if (col === 1) {
    y = rowTop + 42;
  }

  y += 10;
  drawSectionTitle(`Attached Documents (${attachments.length})`);

  if (!attachments.length) {
    drawField('Documents', 'No documents attached');
  } else {
    attachments.forEach((item, index) => {
      ensureSpace(68);
      const label = item.attach_name || `Document ${index + 1}`;
      const note = packageNotes[index] || (
        isExternalLink(item.attach_link)
          ? 'Included in ZIP when downloadable'
          : 'Included in ZIP attachments folder'
      );
      const size = isExternalLink(item.attach_link) ? '-' : formatSize(item.size_bytes);
      const created = formatDate(item.created_at);

      doc.setFillColor(241, 245, 249);
      doc.roundedRect(marginX, y - 12, maxWidth, 52, 4, 4, 'F');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(15, 23, 42);
      doc.text(`${index + 1}. ${label}`, marginX + 10, y + 4);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(71, 85, 105);
      const metaLines = doc.splitTextToSize(
        `${note}  ·  Size: ${size}  ·  Added: ${created}`,
        maxWidth - 20
      );
      doc.text(metaLines, marginX + 10, y + 20);
      y += 40 + metaLines.length * 11;
    });
  }

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(
      `Client Management System · ${client.client_id || ''} · Page ${i} of ${pageCount}`,
      marginX,
      pageHeight - 24
    );
  }

  return doc.output('blob');
}

/**
 * Download a ZIP containing client PDF + attachment files.
 * @param {object} options
 * @param {object} options.client
 * @param {array} options.attachments
 * @param {(attachment) => Promise<{ blob?: Blob, contentType?: string, skipped?: boolean, reason?: string }>} options.fetchAttachment
 */
export async function downloadClientPackage({ client, attachments = [], fetchAttachment }) {
  if (!client) {
    throw new Error('Client details are required');
  }

  const zip = new JSZip();
  const attachmentsFolder = zip.folder('attachments');
  const usedNames = new Set();
  const packageNotes = [];
  const linkIndexLines = [];

  for (let index = 0; index < attachments.length; index += 1) {
    const item = attachments[index];
    const label = item.attach_name || `Document_${index + 1}`;
    const base = safeBaseName(label, `document_${index + 1}`);

    try {
      const result = await fetchAttachment(item);

      if (result?.blob) {
        const ext = guessExtension(item.attach_name, result.contentType, item.attach_link);
        const hasExt = /\.[a-z0-9]{1,8}$/i.test(base);
        const fileName = uniqueZipName(usedNames, hasExt ? base : `${base}${ext || ''}`);
        attachmentsFolder.file(fileName, result.blob);
        packageNotes[index] = `File in ZIP: attachments/${fileName}`;
        linkIndexLines.push(`${index + 1}. ${label} -> attachments/${fileName}`);
      } else if (isExternalLink(item.attach_link)) {
        const linkName = uniqueZipName(usedNames, `${base}.url`);
        const shortcut = `[InternetShortcut]\r\nURL=${item.attach_link}\r\n`;
        attachmentsFolder.file(linkName, shortcut);
        const txtName = uniqueZipName(usedNames, `${base}_link.txt`);
        attachmentsFolder.file(
          txtName,
          `Document: ${label}\nURL: ${item.attach_link}\n\nOpen this link in a browser if the file could not be downloaded automatically.\n`
        );
        packageNotes[index] = `Link saved in ZIP: attachments/${linkName}`;
        linkIndexLines.push(`${index + 1}. ${label} -> ${item.attach_link}`);
      } else {
        packageNotes[index] = result?.reason || 'Could not include this file';
        linkIndexLines.push(`${index + 1}. ${label} -> unavailable`);
      }
    } catch (error) {
      if (isExternalLink(item.attach_link)) {
        const linkName = uniqueZipName(usedNames, `${base}.url`);
        attachmentsFolder.file(linkName, `[InternetShortcut]\r\nURL=${item.attach_link}\r\n`);
        packageNotes[index] = `Link saved in ZIP: attachments/${linkName}`;
        linkIndexLines.push(`${index + 1}. ${label} -> ${item.attach_link}`);
      } else {
        packageNotes[index] = 'Failed to include this attachment';
        linkIndexLines.push(`${index + 1}. ${label} -> failed`);
      }
    }
  }

  if (linkIndexLines.length) {
    zip.file(
      'attachments_index.txt',
      `Attached documents for ${client.client_name} (${client.client_id})\n\n${linkIndexLines.join('\n')}\n`
    );
  }

  const pdfBlob = buildClientPdfBlob(client, attachments, packageNotes);
  const pdfName = `${safeBaseName(client.client_id || client.client_name)}_details.pdf`;
  zip.file(pdfName, pdfBlob);

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  const zipName = `${safeBaseName(client.client_id || client.client_name)}_package.zip`;
  saveAs(zipBlob, zipName);
}

/** @deprecated use downloadClientPackage — kept for compatibility */
export function downloadClientPdf(client, attachments = []) {
  const blob = buildClientPdfBlob(client, attachments);
  const name = `${safeBaseName(client.client_id || client.client_name)}_details.pdf`;
  saveAs(blob, name);
}
